import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {checkOutTimeSelector, getTime} from 'utils/reservation/checkInOutTime';
import {formatClockTime, formatDisplayDate, WEEKDAY_DATE_OPTIONS} from 'utils/dates';
import {useCheckoutSettings} from './useCheckoutSettings';

function toClockLabel(time?: string, language?: string) {
  const clock = formatClockTime(time, language);
  if (!clock) return undefined;

  return [clock.value, clock.meridiem].filter(Boolean).join(' ');
}

function useCheckoutSchedule() {
  const {t, i18n} = useTranslation('check-out');
  const {data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const {checkoutDate, availabilityTime} = useCheckoutSettings();

  const date = checkoutDate ? dayjs(checkoutDate) : null;
  const dateLabel =
    formatDisplayDate(checkoutDate, i18n.language, WEEKDAY_DATE_OPTIONS) ?? '';
  const isToday = Boolean(date?.isSame(dayjs(), 'day'));

  const checkoutTime = toClockLabel(
    reservation && summary
      ? getTime(reservation, summary)(checkOutTimeSelector)
      : undefined,
    i18n.language,
  );

  const todayLabel = checkoutTime
    ? t('check_out_by_time_today', {time: checkoutTime})
    : t('check_out_today');

  const byLabel = isToday
    ? [todayLabel, dateLabel].filter(Boolean).join(' · ')
    : t(checkoutTime ? 'check_out_by_time' : 'check_out_on_date', {
        time: checkoutTime,
        date: dateLabel,
      });

  const opensTime = toClockLabel(availabilityTime, i18n.language);
  const opensLabel = opensTime
    ? t('check_out_opens_at', {date: dateLabel, time: opensTime})
    : t('check_out_opens_on', {date: dateLabel});

  return {dateLabel, byLabel, opensLabel, checkoutTime, isToday};
}

export {useCheckoutSchedule};
